// frontend/src/features/livestock/CompareSlider.tsx
// Two tank shots stacked in one frame with a handle to drag across them
// (design 7.2). Before shows left of the handle, After right of it.
//
// Touch: the frame is `touch-action: pan-y`, and a drag only takes the pointer
// after about 8 px of mostly sideways movement, so scrolling the page over the
// frame still works. A tap jumps the handle there. The handle stays between 4%
// and 96% (clear of the iOS edge back-swipe), its hit target is 44 px wide.
// It starts at 50%; the first time ever it nudges once and says "Drag to
// compare" (no nudge under prefers-reduced-motion), once a photo is on screen;
// until then a loader shows in the frame. Only the primary pointer
// drags: a second finger (or a pinch) is ignored.
//
// Controlled (`position` + `onPositionChange`) so the page and its full-screen
// view share one handle position, or uncontrolled from `defaultPosition`.
//
// Synced zoom (docs/plans/2026-10-29-tank-shot-framing-design.md, 9): both
// shots sit in zoom layers with one transform, inside the slider's clip, so the
// handle stays where it is on screen and the shots can't drift apart. Two
// fingers pinch and pan (a second finger now ends a handle drag rather than
// being ignored); ctrl+wheel and trackpad pinch zoom about the pointer. While
// zoomed, a mouse drag off the handle, or in full screen a finger off the
// handle, pans; a tap there still moves the handle. `zoom` is controlled like
// `position`.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { Box, Center, Loader } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { IconArrowsHorizontal } from '@tabler/icons-react';
import { formatMonth, formatPhotoDay } from './photoInboxFormat';
import { CompareImage, type CompareShotImage } from './CompareImage';
import { NO_ZOOM, zoomTransform, type CompareZoom } from './compareZoom';
import { useZoomGestures } from './useZoomGestures';
import classes from './Compare.module.css';

export const SLIDER_MIN = 4;
export const SLIDER_MAX = 96;
const KEY_STEP = 5;
const PAGE_STEP = 10;
/** Sideways movement before a touch becomes a drag rather than a scroll or tap. */
export const DRAG_THRESHOLD_PX = 8;
export const SLIDER_HINT_KEY = 'rdh-compare-slider-hint-seen';
const HINT_MS = 6000;
const DEFAULT_RATIO = 4 / 3;

export interface CompareSliderProps {
  before: CompareShotImage;
  after: CompareShotImage;
  /** In the full-screen view: a taller frame and pinch zoom allowed. */
  fullScreen?: boolean;
  /** The handle position in percent, when the parent holds it. */
  position?: number;
  /** Where the handle starts when uncontrolled, in percent (50 by default). */
  defaultPosition?: number;
  onPositionChange?: (position: number) => void;
  /** The synced zoom, when the parent holds it. */
  zoom?: CompareZoom;
  onZoomChange?: (zoom: CompareZoom) => void;
  onImageError?: () => void;
  /** Starts each image's alt text: "Tank shot before, 3 Mar 2026". */
  nounTitle?: string;
}

export function clampPosition(position: number): number {
  return Math.min(SLIDER_MAX, Math.max(SLIDER_MIN, position));
}

/**
 * How the two shots are named to a screen reader: their months, their days when
 * both are in one month, or Before and After when both are on one day.
 */
function sideNames(beforeIso: string, afterIso: string): [string, string] {
  const months: [string, string] = [formatMonth(beforeIso), formatMonth(afterIso)];
  if (months[0] !== months[1]) return months;
  const days: [string, string] = [formatPhotoDay(beforeIso), formatPhotoDay(afterIso)];
  if (days[0] !== days[1]) return days;
  return ['Before', 'After'];
}

/**
 * What a screen reader says for the handle: its position, and the shot that
 * covers most of the frame ("60%, Mar 2026 shown"; Before is left of the handle).
 */
export function sliderValueText(position: number, beforeIso: string, afterIso: string): string {
  const value = Math.round(position);
  const [beforeName, afterName] = sideNames(beforeIso, afterIso);
  if (value === 50) return `50%, half ${beforeName} and half ${afterName}`;
  return `${value}%, ${value > 50 ? beforeName : afterName} shown`;
}

function hintSeen(): boolean {
  try {
    return localStorage.getItem(SLIDER_HINT_KEY) === '1';
  } catch {
    // Storage blocked (private mode): the hint shows each time.
    return false;
  }
}

function rememberHint(): void {
  try {
    localStorage.setItem(SLIDER_HINT_KEY, '1');
  } catch {
    // Storage blocked: nothing to remember it in.
  }
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

interface Gesture {
  pointerId: number;
  x: number;
  y: number;
  dragging: boolean;
  /** Became a vertical scroll: ignored from here on. */
  scrolling: boolean;
}

export function CompareSlider({
  before,
  after,
  fullScreen = false,
  position: positionProp,
  defaultPosition = 50,
  onPositionChange,
  zoom: zoomProp,
  onZoomChange,
  onImageError,
  nounTitle = 'Tank shot',
}: CompareSliderProps): JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const [rawPosition, changePosition] = useUncontrolled({
    value: positionProp,
    defaultValue: defaultPosition,
    finalValue: 50,
    onChange: onPositionChange,
  });
  const position = clampPosition(rawPosition);
  const [zoom, changeZoom] = useUncontrolled({
    value: zoomProp,
    defaultValue: NO_ZOOM,
    finalValue: NO_ZOOM,
    onChange: onZoomChange,
  });
  const latestPosition = useRef(position);
  latestPosition.current = position;
  /** The first-time nudge moves only what is drawn, not the value. */
  const [nudge, setNudge] = useState<number | null>(null);
  const [ratio, setRatio] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [showHint] = useState(() => !hintSeen());
  const [hintVisible, setHintVisible] = useState(showHint);
  /** A photo is on screen: the first-time hint waits for it (a slow link would play it over nothing). */
  const [shown, setShown] = useState(false);
  /** Shown, or both photos failed: the loader goes. */
  const [failures, setFailures] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timers = useRef<number[]>([]);

  const setPosition = (next: number): void => {
    changePosition(clampPosition(next));
  };

  /** Any interaction ends the first-time hint and nudge. */
  const stopIntro = useCallback((): void => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
    setAnimating(false);
    setNudge(null);
    setHintVisible(false);
  }, []);

  useEffect(() => {
    if (!showHint || !shown) return undefined;
    rememberHint();
    const later = (ms: number, run: () => void): void => {
      timers.current.push(window.setTimeout(run, ms));
    };
    if (!prefersReducedMotion()) {
      setAnimating(true);
      later(500, () => setNudge(clampPosition(latestPosition.current + 8)));
      later(1000, () => setNudge(null));
      later(1500, () => setAnimating(false));
    }
    later(HINT_MS, () => setHintVisible(false));
    return () => {
      timers.current.forEach((timer) => window.clearTimeout(timer));
      timers.current = [];
    };
  }, [showHint, shown]);

  const positionAt = (clientX: number): number | null => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return null;
    return ((clientX - rect.left) / rect.width) * 100;
  };

  /** The handle jumps to where the pointer is and takes focus, so the arrow keys carry on from it. */
  const jumpTo = (clientX: number): void => {
    const next = positionAt(clientX);
    if (next === null) return;
    stopIntro();
    setPosition(next);
    handleRef.current?.focus({ preventScroll: true });
  };

  /** A second finger: the drag (if any) gives way to the pinch. */
  const dropGesture = (): void => {
    const current = gesture.current;
    gesture.current = null;
    if (current?.dragging) {
      setDragging(false);
      try {
        frameRef.current?.releasePointerCapture(current.pointerId);
      } catch {
        // Already released.
      }
    }
  };

  const zoomGestures = useZoomGestures({
    ref: frameRef,
    zoom,
    onZoomChange: changeZoom,
    preventBrowserZoom: fullScreen,
    panWithOnePointer: (event) =>
      (event.pointerType === 'mouse' || fullScreen) &&
      !handleRef.current?.contains(event.target as Node),
    onPinchStart: () => {
      dropGesture();
      stopIntro();
    },
    onTap: (event) => jumpTo(event.clientX),
  });

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (zoomGestures.pointerDown(event)) return;
    // One gesture at a time.
    if (!event.isPrimary || gesture.current) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    gesture.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      dragging: false,
      scrolling: false,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (zoomGestures.pointerMove(event)) return;
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId || current.scrolling) return;
    if (!current.dragging) {
      const dx = Math.abs(event.clientX - current.x);
      const dy = Math.abs(event.clientY - current.y);
      if (dy > DRAG_THRESHOLD_PX && dy >= dx) {
        current.scrolling = true;
        return;
      }
      if (dx < DRAG_THRESHOLD_PX || dx <= dy) return;
      current.dragging = true;
      try {
        frameRef.current?.setPointerCapture(event.pointerId);
      } catch {
        // The pointer is already gone; the drag ends with it.
      }
      setDragging(true);
      stopIntro();
    }
    const next = positionAt(event.clientX);
    if (next !== null) setPosition(next);
  };

  const endGesture = (event: PointerEvent<HTMLDivElement>, tap: boolean): void => {
    if (zoomGestures.pointerEnd(event, tap)) return;
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    gesture.current = null;
    if (current.dragging) {
      setDragging(false);
      try {
        frameRef.current?.releasePointerCapture(event.pointerId);
      } catch {
        // Already released.
      }
      return;
    }
    if (tap && !current.scrolling) jumpTo(event.clientX);
  };

  /** The browser took the pointer away from the frame (another capture, or it was cancelled). */
  const onLostPointerCapture = (event: PointerEvent<HTMLDivElement>): void => {
    // Only the frame's own capture: taking the pointer from a touch's implicit
    // capture on a child (when the drag starts) bubbles up here too.
    if (event.target !== event.currentTarget) return;
    zoomGestures.forget(event.pointerId);
    if (gesture.current?.pointerId !== event.pointerId) return;
    gesture.current = null;
    setDragging(false);
  };

  /** A pointer that leaves before it drags (a mouse let go outside) ends its gesture. */
  const onPointerLeave = (event: PointerEvent<HTMLDivElement>): void => {
    // Not captured, so its up may land elsewhere: stop counting it toward a pinch.
    if (!frameRef.current?.hasPointerCapture?.(event.pointerId)) {
      zoomGestures.forget(event.pointerId);
    }
    const current = gesture.current;
    if (current?.pointerId === event.pointerId && !current.dragging) gesture.current = null;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const steps: Record<string, number> = {
      ArrowLeft: -KEY_STEP,
      ArrowDown: -KEY_STEP,
      ArrowRight: KEY_STEP,
      ArrowUp: KEY_STEP,
      PageDown: -PAGE_STEP,
      PageUp: PAGE_STEP,
    };
    let next: number | null = null;
    if (event.key in steps) next = position + (steps[event.key] as number);
    else if (event.key === 'Home') next = SLIDER_MIN;
    else if (event.key === 'End') next = SLIDER_MAX;
    if (next === null) return;
    event.preventDefault();
    stopIntro();
    setPosition(next);
  };

  const beforeDay = formatPhotoDay(before.date);
  const afterDay = formatPhotoDay(after.date);
  const frameStyle = {
    '--ratio': String(ratio ?? DEFAULT_RATIO),
    '--pos': `${nudge ?? position}%`,
  } as CSSProperties;
  // The frame takes the After photo's shape; Before is fitted inside it.
  const afterRatio = (value: number): void => setRatio(value);
  const beforeRatio = (value: number): void => setRatio((current) => current ?? value);
  const onShown = (): void => setShown(true);
  const onError = (): void => {
    setFailures((count) => count + 1);
    onImageError?.();
  };
  const loading = !shown && failures < 2;
  const zoomStyle = zoomTransform(zoom);

  return (
    <Box className={classes.wrap} data-fullscreen={fullScreen || undefined}>
      <Box
        ref={frameRef}
        className={classes.frame}
        style={frameStyle}
        data-animating={animating || undefined}
        data-dragging={dragging || undefined}
        data-zoomed={zoom.scale > 1 || undefined}
        data-testid="compare-frame"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(event) => endGesture(event, true)}
        onPointerCancel={(event) => endGesture(event, false)}
        onLostPointerCapture={onLostPointerCapture}
        onPointerLeave={onPointerLeave}
        onDragStart={(event) => event.preventDefault()}
      >
        <Box className={classes.layer}>
          <Box className={classes.zoomLayer} style={zoomStyle} data-testid="compare-zoom-layer">
            <CompareImage
              image={after}
              alt={`${nounTitle} after, ${afterDay}`}
              onRatio={afterRatio}
              onError={onError}
              onShown={onShown}
            />
          </Box>
        </Box>
        <Box className={`${classes.layer} ${classes.before}`}>
          <Box className={classes.zoomLayer} style={zoomStyle} data-testid="compare-zoom-layer">
            <CompareImage
              image={before}
              alt={`${nounTitle} before, ${beforeDay}`}
              onRatio={beforeRatio}
              onError={onError}
              onShown={onShown}
            />
          </Box>
        </Box>

        <span className={classes.label} data-side="before" aria-hidden>
          {beforeDay}
        </span>
        <span className={classes.label} data-side="after" aria-hidden>
          {afterDay}
        </span>

        <div
          ref={handleRef}
          className={classes.handle}
          role="slider"
          tabIndex={0}
          aria-label="Compare before and after"
          aria-orientation="horizontal"
          // 4 and 96 match the clamp; aria-valuetext carries what the position means.
          aria-valuemin={SLIDER_MIN}
          aria-valuemax={SLIDER_MAX}
          aria-valuenow={Math.round(position)}
          aria-valuetext={sliderValueText(position, before.date, after.date)}
          onKeyDown={onKeyDown}
          data-testid="compare-handle"
        >
          <span className={classes.line} />
          <span className={classes.knob}>
            <IconArrowsHorizontal size={16} />
          </span>
        </div>

        {loading && (
          <Center className={classes.loading} data-testid="compare-loading">
            <Loader size="sm" color="gray.3" aria-label="Loading photos" />
          </Center>
        )}
        {hintVisible && shown && <span className={classes.hint}>Drag to compare</span>}
      </Box>
    </Box>
  );
}
